import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip,
  CircularProgress,
  Divider as MuiDivider,
  FormControlLabel,
  Link,
  MenuItem,
  Paper as MuiPaper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import { spacing } from "@mui/system";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useAuth from "../../../../hooks/useAuth";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import useAppSelector from "../../../../hooks/useAppSelector";
import {
  BankTransactionStatus,
  fetchBankTransactions,
} from "../../../../redux/slices/bankTransactions";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

type StatusFilter = "all" | BankTransactionStatus;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Kaikki" },
  { value: "matched", label: "Täsmäytetty" },
  { value: "unmatched", label: "Täsmäyttämätön" },
  { value: "ignored", label: "Ohitettu" },
  { value: "not_applicable", label: "Lähtevät / pankkikulut" },
];

function getStatusChip(status: BankTransactionStatus): {
  label: string;
  color: ChipProps["color"];
} {
  switch (status) {
    case "matched":
      return { label: "Täsmäytetty", color: "success" };
    case "unmatched":
      return { label: "Täsmäyttämätön", color: "warning" };
    case "ignored":
      return { label: "Ohitettu", color: "default" };
    case "not_applicable":
      return { label: "Ei kohdistettavissa", color: "default" };
    default:
      return { label: "Tuotu", color: "info" };
  }
}

export default function BankTransactions() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { transactions, status } = useAppSelector(
    (state) => state.bankTransactions
  );

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  // Hide outgoing payments (debits → NOT_APPLICABLE) by default unless the
  // user explicitly filters to them or toggles them on. Most users only care
  // about incoming credits that should reconcile.
  const [showNotApplicable, setShowNotApplicable] = useState(false);

  useEffect(() => {
    if (!associationId) return;
    dispatch(
      fetchBankTransactions({
        associationId,
        status:
          statusFilter === "all"
            ? undefined
            : (statusFilter as BankTransactionStatus),
        fromDate: fromDate ? format(fromDate, "yyyy-MM-dd") : undefined,
        toDate: toDate ? format(toDate, "yyyy-MM-dd") : undefined,
      })
    );
  }, [dispatch, associationId, statusFilter, fromDate, toDate]);

  // When the user explicitly picks "Lähtevät / pankkikulut", skip the hide
  // filter — otherwise the table would always be empty in that mode.
  const visible = transactions.filter(
    (t) =>
      statusFilter === "not_applicable" ||
      showNotApplicable ||
      t.status !== "not_applicable"
  );

  const isLoading = status === "loading" && transactions.length === 0;

  return (
    <>
      <Helmet title="Pankkitapahtumat" />
      <Typography variant="h3" gutterBottom>
        Pankkitapahtumat
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/accounting/bank/transactions">
          Talous
        </Link>
        <Typography>Pankkitapahtumat</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card mb={6}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              sx={{ minWidth: 160 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            <DatePicker
              label="Alkaen"
              value={fromDate}
              inputFormat="dd.MM.yyyy"
              onChange={(d: Date | null) => setFromDate(d)}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 160 }} />
              )}
            />

            <DatePicker
              label="Päättyen"
              value={toDate}
              inputFormat="dd.MM.yyyy"
              onChange={(d: Date | null) => setToDate(d)}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 160 }} />
              )}
            />

            <FormControlLabel
              control={
                <input
                  type="checkbox"
                  checked={showNotApplicable}
                  onChange={(e) => setShowNotApplicable(e.target.checked)}
                  style={{ marginRight: 4 }}
                  disabled={statusFilter === "not_applicable"}
                />
              }
              label="Näytä myös lähtevät maksut"
            />
          </Box>
        </CardContent>

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Päivämäärä</TableCell>
                <TableCell align="right">Summa (€)</TableCell>
                <TableCell>Vastapuoli</TableCell>
                <TableCell>Viitenumero</TableCell>
                <TableCell>Viesti</TableCell>
                <TableCell>Tila</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Ei tapahtumia
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((tx) => {
                  const { label, color } = getStatusChip(tx.status);
                  return (
                    <TableRow key={tx.id} hover>
                      <TableCell>
                        {format(new Date(tx.bookingDate), "dd.MM.yyyy")}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: tx.amount >= 0 ? "success.main" : "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {tx.amount >= 0 ? "+" : ""}
                        {tx.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {/* Credit: debtor = payer; Debit: creditor = payee. */}
                        {(tx.amount >= 0 ? tx.debtorName : tx.creditorName) ??
                          "—"}
                      </TableCell>
                      <TableCell>
                        {tx.referenceNumber ? (
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {tx.referenceNumber}
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tx.remittanceInfo ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={label} color={color} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      </Card>
    </>
  );
}
